'use client';

import { ReactNode, useMemo } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { TAnalyticArgs } from '@/analytics';
import { useChatData } from '@/containers/ChatContainer';
import { Spinner, TIconName } from '@mofid-securities/uikit';
import { openLogoutBottomSheetContent } from '@/components/LogoutBottomSheet';
import PulseBadge from '@/components/PulseBadge';
import { IUserStatusOnClickWrapperProps } from '@/userStatus/components';
import { useIsMofidCardDisabled } from '@/hooks/isMofidCardDisabled';
import { useGetUserStatus } from '@/userStatus/hooks';

export interface IRightSidebarItem {
  id?: string;
  title: string;
  href?: string;
  onClick?: () => void;
  icon: TIconName | ReactNode;
  activeIcon: IRightSidebarItem['icon'];
  analytics?: TAnalyticArgs;
  badge?: ReactNode;
  isDisabled?: boolean;
  checkActive: (
    __item: Omit<IRightSidebarItem, 'checkActive'>
  ) => boolean | undefined;
  isHidden?: boolean;
  forbiddenStatuses?: IUserStatusOnClickWrapperProps['forbiddenStatuses'];
}

const useRightSidebarItems = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { isAnonymous, isVerified } = useGetUserStatus();
  const chatData = useChatData();
  const { isMofidCardDisabled } = useIsMofidCardDisabled();

  return useMemo(() => {
    const items: IRightSidebarItem[] = [
      {
        id: 'navbar_click_home',
        title: 'خانه',
        href: '/',
        icon: 'home_outline',
        activeIcon: 'home',
        checkActive: (item) =>
          pathname === item.href && searchParams?.get('index') !== '1',
        analytics: ['NAVBAR_HOME'],
      },
      {
        title: 'مفیدکارت',
        href: '/?index=1',
        icon: 'mofid_card_outline',
        id: 'navbar_click_card',
        analytics: ['navbar_click_card'],
        activeIcon: 'mofid_card',
        isHidden: isMofidCardDisabled,
        checkActive: () =>
          (pathname === '/' && searchParams?.get('index') === '1') ||
          pathname?.startsWith('/card'),
      },
      {
        id: 'navbar_click_funds',
        title: 'صندوق‌های مفید',
        href: '/invest/fund',
        icon: 'investment_outline',
        activeIcon: 'investment',
        checkActive: (item) =>
          pathname?.startsWith(`${item?.href}`) ||
          pathname?.startsWith(`/invest/etf`),
        analytics: ['NAVBAR_FUNDS'],
      },
      {
        id: 'Navbar_Click_Stocks',
        title: 'سبد سهام',
        href: '/stock',
        icon: 'stocks_outline',
        activeIcon: 'stocks',
        checkActive: (item) => pathname?.startsWith(`${item?.href}`),
        analytics: ['Navbar_Click_Stocks'],
      },
      {
        id: 'navbar_click_PFM',
        title: 'مدیریت مالی',
        href: '/pfm',
        icon: 'pfm_outline',
        activeIcon: 'pfm',
        checkActive: (item) => pathname?.startsWith(`${item?.href}`),
        analytics: ['NAVBAR_PFM'],
        forbiddenStatuses: ['GUEST', 'ANONYMOUS'],
      },
      {
        title: 'پشتیبانی هوشمند',
        href: undefined,
        isDisabled: !chatData.isEnabled,
        icon: chatData.isLoading ? <Spinner size={20} /> : 'ai_support',
        activeIcon: 'ai_support',
        checkActive: (__item) => false,
        onClick: () => chatData.toggleVisibility(),
        badge: !!chatData.totalUnread && (
          <PulseBadge className="absolute right-[25px] top-[7px] lg:right-[15px] lg:top-[8px]" />
        ),
        analytics: undefined, // it is handled inside toggleVisibility
      },
      {
        title: 'پروفایل کاربر',
        href: '/profile',
        icon: 'profile_outline',
        activeIcon: 'profile',
        checkActive: (item) => pathname?.startsWith(`${item?.href}`),
        analytics: ['PROFILE', { reference: document.title, platform: 'WEB' }],
        isHidden: !isVerified,
      },
      {
        title: 'خروج از حساب',
        href: undefined,
        icon: 'logout',
        activeIcon: 'logout',
        checkActive: (__item) => false,
        onClick: openLogoutBottomSheetContent,
        isHidden: isAnonymous,
      },
    ];
    return items;
  }, [pathname, searchParams, chatData, isAnonymous, isVerified]);
};

export default useRightSidebarItems;
